import type {
  AlignmentView,
  CoupleView,
  DeckCard,
  DeckSource,
  PresenceView,
  MatchesScreen,
  PendingFeedback,
  Provider,
  SearchResult,
  SeenEntry,
  SwipeDirection,
  Theme,
  TitleDetails,
  WishlistEntry,
} from "@hyggebam/shared";

/** Typed wrapper over the hyggebam HTTP API (same-origin via the Vite proxy). */

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiError(res.status, (detail as any).error ?? res.statusText);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface OpenResponse {
  couple: CoupleView;
  pending_feedback: PendingFeedback | null;
}

export interface CountryResponse {
  wasCold: boolean;
  view: CoupleView;
}

export interface SwipeResponse {
  state: string;
  matchFormed: { tmdb_id: number; title: string } | null;
}

export const api = {
  createCouple: (names: string[], country: string) => req<CoupleView>("POST", "/api/couples", { names, country }),
  open: (token: string) => req<OpenResponse>("GET", `/api/c/${token}`),
  providers: (token: string) => req<Provider[]>("GET", `/api/c/${token}/providers`),
  setCountry: (token: string, country: string) => req<CountryResponse>("POST", `/api/c/${token}/country`, { country }),
  setServices: (token: string, services: string[]) => req<CoupleView>("POST", `/api/c/${token}/services`, { services }),
  setLanguage: (token: string, language: string) => req<CoupleView>("POST", `/api/c/${token}/language`, { language }),
  rename: (token: string, personId: string, name: string) =>
    req<CoupleView>("POST", `/api/c/${token}/seats/${personId}/rename`, { name }),
  regenerate: (token: string) => req<CoupleView>("POST", `/api/c/${token}/regenerate`),
  deck: (token: string, personId: string, limit = 15, source: DeckSource = "all") =>
    req<{ cards: DeckCard[] }>("GET", `/api/c/${token}/deck?personId=${personId}&limit=${limit}&source=${source}`),
  search: (token: string, q: string) =>
    req<{ results: SearchResult[] }>("GET", `/api/c/${token}/search?q=${encodeURIComponent(q)}`),
  wishlist: (token: string) => req<{ items: WishlistEntry[] }>("GET", `/api/c/${token}/wishlist`),
  addWishlist: (token: string, personId: string, tmdbId: number) =>
    req<{ items: WishlistEntry[] }>("POST", `/api/c/${token}/wishlist`, { personId, tmdbId }),
  removeWishlist: (token: string, tmdbId: number) =>
    req<{ items: WishlistEntry[] }>("POST", `/api/c/${token}/wishlist/${tmdbId}/remove`),
  title: (token: string, tmdbId: number) => req<TitleDetails>("GET", `/api/c/${token}/title/${tmdbId}`),
  generateTheme: (token: string, personId: string, prompt: string) =>
    req<Theme>("POST", `/api/c/${token}/theme/generate`, { personId, prompt }),
  generateThemeVariants: (token: string, personId: string, prompt: string, count = 2) =>
    req<{ variants: Theme[] }>("POST", `/api/c/${token}/theme/variants`, { personId, prompt, count }),
  getTheme: (token: string, id: string) => req<Theme>("GET", `/api/c/${token}/theme/${id}`),
  setCoupleTheme: (token: string, themeId: string | null) =>
    req<CoupleView>("POST", `/api/c/${token}/theme/couple`, { themeId }),
  markWatched: (token: string, personId: string, tmdbId: number, rating?: number) =>
    req<{ ok: true }>("POST", `/api/c/${token}/watched`, { personId, tmdbId, ...(rating ? { rating } : {}) }),
  seen: (token: string, personId: string) =>
    req<{ items: SeenEntry[] }>("GET", `/api/c/${token}/seen?personId=${personId}`),
  swipe: (token: string, personId: string, tmdbId: number, direction: SwipeDirection) =>
    req<SwipeResponse>("POST", `/api/c/${token}/swipe`, { personId, tmdbId, direction }),
  undo: (token: string, personId: string, tmdbId: number) =>
    req<{ ok: true }>("POST", `/api/c/${token}/undo`, { personId, tmdbId }),
  matches: (token: string) => req<MatchesScreen>("GET", `/api/c/${token}/matches`),
  alignment: (token: string) => req<AlignmentView>("GET", `/api/c/${token}/alignment`),
  presence: (token: string, personId: string) => req<PresenceView>("GET", `/api/c/${token}/presence?personId=${personId}`),
  choose: (token: string, tmdbId: number) => req<{ ok: true }>("POST", `/api/c/${token}/matches/${tmdbId}/choose`),
  feedback: (token: string, tmdbId: number, outcome: "watched" | "abandoned" | "not_yet", ratings?: Record<string, number>) =>
    req<{ ok: true }>("POST", `/api/c/${token}/feedback`, { tmdbId, outcome, ratings }),
  importCsv: (token: string, personId: string, source: "imdb_ratings" | "imdb_watchlist", csv: string) =>
    req<{ job_id: string }>("POST", `/api/c/${token}/import`, { personId, source, csv }),
  importStatus: (token: string, jobId: string) =>
    req<{ status: string; stats: { rows_matched: number; rows_failed: number; rows_parsed: number } }>(
      "GET",
      `/api/c/${token}/import/${jobId}`,
    ),
};
